/**
 * TRUS-727 — the controls above the MIF layout list.
 *
 * Two views:
 *   mine     only this organization's own layouts and the ones it has installed a
 *            pack from (`is_mine`). The default once there is anything to show, so
 *            the screen opens on what the org actually uses.
 *   catalog  every installable layout, narrowed by state (default CA) and then by
 *            plan. A layout has no state of its own: the backend reads it from the
 *            plan's payer (plan_key = lower(payers.payer_id) -> states_covered).
 */
import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { SearchableSelect } from '../ui/searchable-select';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { MifLayout } from '@/services/api/mifImport';

export type LayoutView = 'mine' | 'catalog';

export const DEFAULT_STATE = 'CA';
export const ALL_PLANS = '';

/** States offered in the picker: every state any listed plan covers, plus the default. */
export const statesFrom = (layouts: MifLayout[]): string[] =>
  Array.from(new Set([DEFAULT_STATE, ...layouts.flatMap((l) => l.states || [])])).sort();

/** Plans offered once a state is chosen, as [plan_key, plan_name]. */
export const plansIn = (layouts: MifLayout[], state: string): [string, string][] => {
  const seen = new Map<string, string>();
  layouts
    .filter((l) => (l.states || []).includes(state))
    .forEach((l) => {
      if (!seen.has(l.plan_key)) seen.set(l.plan_key, l.plan_name);
    });
  return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
};

export const mineOnly = (layouts: MifLayout[]): MifLayout[] => layouts.filter((l) => l.is_mine);

export const MifLayoutCatalogControls: React.FC<{
  view: LayoutView;
  onViewChange: (view: LayoutView) => void;
  mineCount: number;
  states: string[];
  state: string;
  onStateChange: (state: string) => void;
  plans: [string, string][];
  plan: string;
  onPlanChange: (plan: string) => void;
}> = ({ view, onViewChange, mineCount, states, state, onStateChange, plans, plan, onPlanChange }) => {
  const { t } = useAppTranslation();

  return (
    <div className="space-y-3">
      <Tabs value={view} onValueChange={(v) => onViewChange(v as LayoutView)}>
        <TabsList>
          <TabsTrigger value="mine">
            {t('integrations.mif_layouts.my_layouts', 'My Layouts')} ({mineCount})
          </TabsTrigger>
          <TabsTrigger value="catalog">{t('integrations.mif_layouts.catalog', 'Catalog')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'mine' ? (
        <p className="text-sm text-muted-foreground">
          {t(
            'integrations.mif_layouts.mine_help',
            "Your organization's own layouts, and the ones you have installed. Browse the catalog to add a plan."
          )}
        </p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="mif-catalog-state" className="text-xs font-medium text-muted-foreground">
              {t('integrations.mif_layouts.state', 'State')}
            </label>
            <SearchableSelect
              id="mif-catalog-state"
              options={states.map((s) => ({ value: s, label: s }))}
              value={state}
              onValueChange={(v) => onStateChange(v || DEFAULT_STATE)}
              searchPlaceholder={t('integrations.mif_layouts.search_state', 'Type a state…')}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="mif-catalog-plan" className="text-xs font-medium text-muted-foreground">
              {t('integrations.mif_layouts.plan', 'Health plan')}
            </label>
            <SearchableSelect
              id="mif-catalog-plan"
              options={[
                { value: ALL_PLANS, label: t('integrations.mif_layouts.all_plans', 'All plans') },
                ...plans.map(([key, name]) => ({ value: key, label: name })),
              ]}
              value={plan}
              onValueChange={onPlanChange}
              placeholder={t('integrations.mif_layouts.all_plans', 'All plans')}
              searchPlaceholder={t('integrations.mif_layouts.search_plan', 'Type a plan name…')}
              emptyText={t('integrations.mif_layouts.no_plans', 'No plans in this state')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MifLayoutCatalogControls;
